import { useQuery } from '@tanstack/react-query';
import { api, podatki } from '../api/odjemalec';
import { glave, obNapaki } from '../skrbnistvo/zeton';

/** Oznaka in barva stanja povratne informacije (NOVO / OGLEDANO / ZAKLJUCENO), enako pri stranki in v skrbništvu. */
export function statusPovratne(status: string | undefined): { naziv: string; barva: string } {
  switch (status) {
    case 'NOVO':
      return { naziv: 'novo', barva: 'red' };
    case 'ZAKLJUCENO':
      return { naziv: 'zaključeno', barva: 'green' };
    default:
      return { naziv: 'ogledano', barva: 'yellow' };
  }
}

/** Del besedila sporočila: navadno besedilo ali povezava ({@code povezava} je vedno naslov http/https). */
export type DelBesedila = { besedilo: string; povezava?: string };

const POVEZAVA = /(?<![\w@/.])(?:https?:\/\/|www\.)[^\s<>"]+/gi;

/** Brez ločil na koncu (»…/nov-kupec.«), zaklepaj ostane, če ima povezava svoj oklepaj (wiki/Foo_(bar)). */
function brezLocil(url: string): string {
  let u = url;
  for (;;) {
    const z = u.at(-1) ?? '';
    const zaklepaj = z === ')' && u.split('(').length < u.split(')').length;
    if (!zaklepaj && (z === ')' || !/[.,;:!?»«"'\]]/.test(z))) {
      return u;
    }
    u = u.slice(0, -1);
  }
}

/**
 * Razdeli besedilo na navadne dele in povezave (http://, https:// ali www.), da jih pogovor prikaže kot klikljive
 * povezave; prikaz ostane besedilo (brez HTML), zato vsebina sporočila ne more vstaviti oznak.
 */
export function deliBesedila(besedilo: string): DelBesedila[] {
  const deli: DelBesedila[] = [];
  let zadnji = 0;
  for (const m of besedilo.matchAll(POVEZAVA)) {
    const url = brezLocil(m[0]);
    // sam »https://« ali »www.« ni povezava
    if (!/^(?:https?:\/\/|www\.)\S/i.test(url)) {
      continue;
    }
    if (m.index > zadnji) {
      deli.push({ besedilo: besedilo.slice(zadnji, m.index) });
    }
    deli.push({ besedilo: url, povezava: /^www\./i.test(url) ? `https://${url}` : url });
    zadnji = m.index + url.length;
  }
  if (zadnji < besedilo.length) {
    deli.push({ besedilo: besedilo.slice(zadnji) });
  }
  return deli;
}

/** »1 nov odgovor«, »2 nova odgovora«, »3 novi odgovori«, »5 novih odgovorov« (dvojina in množina). */
export function noviOdgovori(n: number): string {
  const d = n % 100;
  return d === 1
    ? `${n} nov odgovor`
    : d === 2
      ? `${n} nova odgovora`
      : d === 3 || d === 4
        ? `${n} novi odgovori`
        : `${n} novih odgovorov`;
}

/** »1 sporočilo«, »2 sporočili«, »3 sporočila«, »5 sporočil«. */
export function sporocil(n: number): string {
  const d = n % 100;
  return `${n} ${d === 1 ? 'sporočilo' : d === 2 ? 'sporočili' : d === 3 || d === 4 ? 'sporočila' : 'sporočil'}`;
}

/** Število niti z neprebranim odgovorom (oznaka na začetnem zaslonu), osveženo vsakih 5 minut in ob vrnitvi na zavihek. */
export function useNeprebraniOdgovori() {
  return useQuery({
    queryKey: ['povratne-stevilo'],
    queryFn: ({ signal }) => podatki(api.GET('/api/povratne-informacije/stevilo', { signal })),
    refetchInterval: 300_000,
    refetchOnWindowFocus: true,
  });
}

/** Skrbništvo: število povratnih informacij v stanju NOVO (oznaka na zavihku), osveženo vsaki 2 minuti. */
export function useNovePovratne() {
  return useQuery({
    queryKey: ['skrbnik', 'povratne-stevilo'],
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/povratne-informacije/stevilo', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
    refetchInterval: 120_000,
  });
}
